import { useState, useEffect, useCallback } from 'react';
import useGameState from './hooks/useGameState';
import useUsers from './hooks/useUsers';
import useInvites from './hooks/useInvites';
import useJoinRequests from './hooks/useJoinRequests';
import { useAuthContext } from './contexts/AuthContext';
import { partnerThemes } from './services/themes';
import SetupView from './components/SetupView';
import GamePhotoView from './components/GamePhotoView';
import GameLobbyView from './components/GameLobbyView';
import GameView from './components/GameView';
import VictoryView from './components/VictoryView';
import SpectatorView from './components/SpectatorView';
import InviteBanner from './components/InviteBanner';
import JoinRequestBanner from './components/JoinRequestBanner';
import RulesSidebar from './components/modals/RulesSidebar';
import RewardModal from './components/modals/RewardModal';
import './App.css';

export default function App() {
  const [activePartner] = useState('beaumonts');
  const [spectatorCode, setSpectatorCode] = useState(null);
  const [sentInvites, setSentInvites] = useState([]);
  const { allUsers, createUser } = useUsers();
  const { linkedProfile } = useAuthContext();
  const game = useGameState();
  const { pendingInvites, sendInvite, acceptInvite, declineInvite } = useInvites(linkedProfile?.id);
  const { pendingJoinRequests, acceptJoinRequest, declineJoinRequest } = useJoinRequests(game.currentGameId);

  const theme = partnerThemes[activePartner] || partnerThemes.default;
  const themeClass = theme.themeClass;
  const venueName = theme.venueName;

  // Check for ?game= param or shared link on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gameParam = params.get('game');
    if (gameParam) {
      setSpectatorCode(gameParam.toUpperCase());
      return;
    }

    function isFromSharedLink() {
      if (!document.referrer) return false;
      try {
        return new URL(document.referrer).hostname !== window.location.hostname;
      } catch {
        return false;
      }
    }

    if (isFromSharedLink()) {
      localStorage.removeItem('activeGameId');
    } else {
      game.resumeActiveGame();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleQuit() {
    if (confirm("Are you sure you want to quit the game?")) {
      game.returnToLobby();
    }
  }

  function handleJoinGame(code) {
    setSpectatorCode(code);
    window.history.replaceState({}, '', `/?game=${code}`);
  }

  function handleLeaveSpectator() {
    setSpectatorCode(null);
    window.history.replaceState({}, '', '/');
  }

  const handleInvite = useCallback(async (toUserId, toUserName) => {
    if (!game.currentGameId || !game.gameCode || !linkedProfile) return;
    await sendInvite(game.currentGameId, game.gameCode, linkedProfile.name, toUserId, toUserName);
    setSentInvites(prev => [...prev, { toUserId, toUserName }]);
  }, [game.currentGameId, game.gameCode, linkedProfile, sendInvite]);

  // Wraps game.startGame so we can auto-dispatch invites for any player flagged
  // as "Own phone" (isRemote) who has a profile. Players without a profile fall
  // back to the existing share-the-game-code flow.
  const handleStartGame = useCallback(async (gamePlayers, partner) => {
    const result = await game.startGame(gamePlayers, partner);
    if (!result) return null;
    const { gameId, gameCode } = result;
    const fromName = linkedProfile?.name || 'Host';
    const remoteWithProfile = gamePlayers.filter(
      p => p.isRemote && p.userId && p.userId !== linkedProfile?.id
    );
    for (const p of remoteWithProfile) {
      try {
        await sendInvite(gameId, gameCode, fromName, p.userId, p.name);
        setSentInvites(prev => [...prev, { toUserId: p.userId, toUserName: p.name }]);
      } catch (e) {
        console.warn('Auto-invite failed for', p.name, e);
      }
    }
    return gameId;
  }, [game, linkedProfile, sendInvite]);

  function handleAcceptInvite(invite) {
    acceptInvite(invite.id);
    handleJoinGame(invite.gameCode);
  }

  // Host accepts/declines a join request from a spectator. accept needs the
  // current player array because we have to write the full updated list.
  const handleAcceptJoinRequest = useCallback((request) => {
    acceptJoinRequest(request, game.players);
  }, [acceptJoinRequest, game.players]);

  // Spectator mode takes priority
  if (spectatorCode) {
    return (
      <div className={`phone-screen ${themeClass}`}>
        <SpectatorView code={spectatorCode} onLeave={handleLeaveSpectator} />
      </div>
    );
  }

  return (
    <div className={`phone-screen ${themeClass}`}>
      <RulesSidebar />

      {game.view === 'setup' && (
        <>
          <InviteBanner
            invites={pendingInvites}
            onAccept={handleAcceptInvite}
            onDecline={declineInvite}
          />
          <SetupView
            allUsers={allUsers}
            onStartGame={handleStartGame}
            onCreateUser={createUser}
            onJoinGame={handleJoinGame}
          />
        </>
      )}

      {game.view === 'photo' && (
        <GamePhotoView
          hostName={game.players[0]?.name || 'Host'}
          onPhotoTaken={game.uploadGamePhoto}
          onSkip={game.skipGamePhoto}
        />
      )}

      {game.view === 'lobby' && (
        <>
          <JoinRequestBanner
            requests={pendingJoinRequests}
            onAccept={handleAcceptJoinRequest}
            onDecline={declineJoinRequest}
          />
          <GameLobbyView
            gameCode={game.gameCode}
            gameId={game.currentGameId}
            players={game.players}
            allUsers={allUsers}
            hostName={linkedProfile?.name || 'Host'}
            onStart={game.startFromLobby}
            onCancel={game.returnToLobby}
            onInvite={handleInvite}
            sentInvites={sentInvites}
          />
        </>
      )}

      {game.view === 'game' && (
        <>
          <JoinRequestBanner
            requests={pendingJoinRequests}
            onAccept={handleAcceptJoinRequest}
            onDecline={declineJoinRequest}
          />
        <GameView
          players={game.players}
          currentPlayerIndex={game.currentPlayerIndex}
          turnNumber={game.turnNumber}
          currentScore={game.currentScore}
          isFarkle={game.isFarkle}
          gamePhotoUrl={game.gamePhotoUrl}
          gameCode={game.gameCode}
          onNumKey={game.handleNumKey}
          onDelete={game.handleDelete}
          onQuickAdd={game.handleQuickAdd}
          onBank={game.bankScore}
          onFarkle={game.handleFarkle}
          onUndo={game.undoLastAction}
          canUndo={game.canUndo}
          onQuit={handleQuit}
          redemption={game.redemption}
          awaitingInheritChoice={game.awaitingInheritChoice}
          onChooseInherit={game.chooseInherit}
          onChooseFresh={game.chooseFresh}
        />
        </>
      )}

      {game.view === 'victory' && (
        <VictoryView
          players={game.players}
          currentPlayerIndex={game.currentPlayerIndex}
          venueName={venueName}
          activePartner={activePartner}
          onSaveToVault={game.saveToVault}
          onReturnToLobby={game.returnToLobby}
        />
      )}

      <RewardModal
        show={game.showReward}
        onClose={() => game.setShowReward(false)}
        playerName={game.rewardPlayerName}
        timeText={game.rewardTimeText}
        venueName={venueName}
      />
    </div>
  );
}
